/* eslint-disable camelcase  */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
// material
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Box,
  OutlinedInput,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
// components
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import useAxiosWithAuth from '../api/useAxiosWithAuth';
import { ExporterMoreMenu } from '../sections/@dashboard/exporter';

const ManageExporters = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [addExporterDialogOpen, setAddExporterDialogOpen] = useState(false);
  const [newExporterName, setNewExporterName] = useState('');
  const [newExporterNameError, setNewExporterNameError] = useState(false);
  const [newExporterNameHelperText, setNewExporterNameHelperText] = useState('');
  const axiosInstance = useAxiosWithAuth();

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        // Fetch exporters from API with pagination parameters
        const response = await axiosInstance.get(
          `/trade/exporter-name?page_size=${rowsPerPage}&page_number=${page + 1}&search=${filterValue}`
        );

        if (response.status === 200) {
          // The new response structure has meta and payload
          const { payload: data, meta } = response.data;
          setTotal(meta.total_size || data.length); // Set total for pagination from meta

          // Add serial numbers based on pagination
          const formattedData = data.map((item, index) => ({
            ...item,
            sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
          }));

          setTableData(formattedData);
        } else {
          setTableData([]);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setTableData([]);
        console.error('Error fetching exporters:', error);
      }
    };

    getData();
  }, [rowsPerPage, page, filterValue]);

  useEffect(() => {
    return setPage(0);
  }, []);

  // Simple CSV export functionality
  const onBtnExport = useCallback(() => {
    // Create CSV content
    const headers = ['SL', 'Name'];
    const rows = tableData.map((row) => [row.sl, row.name]);
    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'exporters.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [tableData]);

  const handleAddExporterClick = () => {
    setAddExporterDialogOpen(true);
    setNewExporterName('');
    setNewExporterNameError(false);
    setNewExporterNameHelperText('');
  };

  const handleAddExporterDialogClose = () => {
    setAddExporterDialogOpen(false);
    setNewExporterName('');
    setNewExporterNameError(false);
    setNewExporterNameHelperText('');
  };

  const handleAddExporter = async () => {
    // Basic validation
    if (!newExporterName.trim()) {
      setNewExporterNameError(true);
      setNewExporterNameHelperText('Exporter name is required');
      return;
    }

    if (newExporterName.trim().length < 2) {
      setNewExporterNameError(true);
      setNewExporterNameHelperText('Exporter name must be at least 2 characters');
      return;
    }

    try {
      // Make the POST API call to create the exporter
      const response = await axiosInstance.post('/trade/exporter-name', {
        name: newExporterName.trim(),
      });

      // Check if the API call was successful
      if (response.status === 200 || response.status === 201) {
        // Create new exporter object based on the API response
        const newExporter = {
          id: response.data.id || tableData.length + 1, // Use ID from response or generate one
          sl: tableData.length + 1, // Simple serial number for the new item
          name: newExporterName.trim(),
        };

        // Add to the beginning of the table data
        setTableData((prevData) => [newExporter, ...prevData]);

        // Show success message
        toast.success(`Exporter "${newExporterName}" added successfully`);

        // Close the dialog
        setAddExporterDialogOpen(false);
        setNewExporterName('');
        setNewExporterNameError(false);
        setNewExporterNameHelperText('');
      } else {
        // Handle non-success responses
        toast.error('Failed to add exporter. Please try again.');
      }
    } catch (error) {
      console.error('Error adding exporter:', error);
      // Check if the error response has the specific error message from the API
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add exporter. Please try again.');
      }
    }
  };

  return (
    <Page title="Manage Exporters">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} ml={2}>
          <Typography variant="h4" gutterBottom>
            Manage Exporters
          </Typography>
          <Button variant="contained" sx={{ mr: 2 }} onClick={handleAddExporterClick}>
            Add New Exporter
          </Button>
        </Stack>

        <Card>
          {/* 
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '16px',
            }}
          >
            <OutlinedInput
              size={'small'}
              sx={{ my: 2, ml: 2, flex: 1, maxWidth: '400px' }}
              id="outlined-adornment-weight"
              value={filterValue}
              placeholder="Search exporters"
              onChange={(e) => setFilterValue(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </InputAdornment>
              }
              aria-describedby="outlined-weight-helper-text"
              inputProps={{
                'aria-label': 'weight',
              }}
            />
            <Stack direction="row" spacing={2} sx={{ my: 2, mx: 2 }}>
              <Button
                variant="outlined"
                onClick={onBtnExport}
                startIcon={<Iconify icon="bxs:export" width={20} height={20} />}
              >
                Export
              </Button>
            </Stack>
          </div>
          */}

          <TableContainer component={Paper}>
            <Table size={'small'} sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">#SL</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  if (isLoading) {
                    return (
                      <TableRow>
                        <TableCell align="center" colSpan={3}>
                          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ p: 3 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ ml: 2 }}>
                              Loading exporters...
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  if (tableData.length === 0) {
                    return (
                      <TableRow>
                        <TableCell align="center" colSpan={3}>
                          <Typography variant="subtitle1" color="textSecondary" sx={{ py: 3 }}>
                            No Rows To Show
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return tableData.map((exporter, index) => (
                    <TableRow key={exporter.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle1">{exporter.name}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <ExporterMoreMenu
                          id={exporter.id}
                          name={exporter.name}
                          showDelete={false} /* Only hide delete option */
                          onActionComplete={() => {
                            // Refresh data after action
                            const getData = async () => {
                              try {
                                setIsLoading(true);
                                // Fetch exporters from API with pagination parameters
                                const response = await axiosInstance.get(
                                  `/trade/exporter-name?page_size=${rowsPerPage}&page_number=${
                                    page + 1
                                  }&search=${filterValue}`
                                );

                                if (response.status === 200) {
                                  // The new response structure has meta and payload
                                  const { payload: data, meta } = response.data;
                                  setTotal(meta.total_size || data.length); // Set total for pagination from meta

                                  // Add serial numbers based on pagination
                                  const formattedData = data.map((item, index) => ({
                                    ...item,
                                    sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
                                  }));

                                  setTableData(formattedData);
                                } else {
                                  setTableData([]);
                                }
                                setIsLoading(false);
                              } catch (error) {
                                setIsLoading(false);
                                setTableData([]);
                                console.error('Error fetching exporters:', error);
                              }
                            };

                            getData();
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              rowsPerPageOptions={[5, 10, 25]}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Card>

        {/* Add Exporter Dialog */}
        <Dialog open={addExporterDialogOpen} onClose={handleAddExporterDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Exporter</DialogTitle>
          <DialogContent dividers>
            <TextField
              autoFocus
              margin="dense"
              id="exporter-name"
              label="Exporter Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newExporterName}
              onChange={(e) => {
                setNewExporterName(e.target.value);
                // Clear error when user starts typing
                if (newExporterNameError) {
                  setNewExporterNameError(false);
                  setNewExporterNameHelperText('');
                }
              }}
              error={newExporterNameError}
              helperText={newExporterNameHelperText}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddExporterDialogClose}>Cancel</Button>
            <Button onClick={handleAddExporter} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default ManageExporters;
